import { jsx, jsxs } from 'react/jsx-runtime';
import { Flex } from '@fabric-app/ui/components/Flex';
import { FloatAnchor } from '@fabric-app/ui/components/FloatAnchor';
import { mediaHover, mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { NodeViewWrapper } from '@tiptap/react';
import { useState, useRef, useMemo } from 'react';
import { styled } from 'styled-components';

const Container = styled(Flex).attrs((p) => {
  return {
    gap: "element",
    alignItems: "center",
    ...p
  };
}).withConfig({
  displayName: "EmptyComponent__Container",
  componentId: "sc-dea5327d-0"
})([
  `flex-grow:1;background:`,
  `;border-radius:4px;padding:12px;color:`,
  `;cursor:pointer;`,
  `{&:hover{background:`,
  `;}}`
], cssVar["color-bg-tertiary"], cssVar["color-text-tertiary"], mediaHover, cssVar["color-bg-quarternary"]);
const FloatingContent = styled(Flex).withConfig({
  displayName: "EmptyComponent__FloatingContent",
  componentId: "sc-dea5327d-1"
})([
  `border-radius:8px;padding:12px;background:`,
  `;color:`,
  `;border:1px solid `,
  `;z-index:125;pointer-events:auto;`,
  `{width:100%;height:355px;}`
], cssVar["color-bg-secondary"], cssVar["color-text-tertiary"], cssVar["color-border-primary"], mediaMobile);
const Dropzone = styled.button.withConfig({
  displayName: "EmptyComponent__Dropzone",
  componentId: "sc-dea5327d-2"
})([
  `width:228px;height:228px;padding:1rem;display:flex;align-items:center;justify-content:center;text-align:center;text-justify:center;text-wrap:balance;border-radius:13px;border:1px dashed var(--fabric-color-border-primary);color:rgba(var(--fabric-color-text-primary-rgb),0.6);cursor:pointer;transition:border-color 0.2s ease-in-out,color 0.2s ease-in-out;&:hover,&[data-hover='true']{border-color:var(--fabric-color-app-primary-text);color:var(--fabric-color-app-primary-text);}`,
  `{width:100%;}`
], mediaMobile);
const EmptyComponent = ({ editor, node, getPos, type, accept, icon, label }) => {
  const [open, setOpen] = useState(false);
  const { isMobileView } = useResponsive();
  const inputRef = useRef(null);
  const onClickDropZone = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };
  const [filesOver, setFilesOver] = useState(false);
  const handleDragEnter = (e) => {
    e.preventDefault();
    setFilesOver(true);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    setFilesOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setFilesOver(false);
  };
  const handleUpload = (file) => {
    if (!file || !editor.isEditable) return;
    setOpen(false);
    const pos = getPos();
    editor.chain().focus().deleteRange({
      from: pos,
      to: pos + node.nodeSize
    }).run();
    editor.can().uploadResource(file);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
    setFilesOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files[0]);
    }
  };
  const dropZoneText = useMemo(() => {
    if (isMobileView) return `Click to upload ${type}`;
    return filesOver ? `Drop to upload ${type}` : `Drop or click to upload ${type}`;
  }, [
    filesOver,
    isMobileView,
    type
  ]);
  const container = editor.view.dom.closest("[data-tiptap-container]");
  return /* @__PURE__ */ jsx(NodeViewWrapper, {
    children: /* @__PURE__ */ jsxs(FloatAnchor.Root, {
      offset: 10,
      placement: "bottom",
      open: !editor.isEditable ? false : open,
      setOpen,
      children: [
        /* @__PURE__ */ jsx(FloatAnchor.Trigger, {
          asChild: true,
          action: "click",
          preventDefault: true,
          children: /* @__PURE__ */ jsxs(Container, {
            children: [
              icon,
              " ",
              label
            ]
          })
        }),
        /* @__PURE__ */ jsx(FloatAnchor.Floating, {
          asChild: true,
          overlay: !isMobileView,
          mobileNonAnchored: true,
          portalLocation: isMobileView ? container : void 0,
          children: /* @__PURE__ */ jsxs(FloatingContent, {
            children: [
              /* @__PURE__ */ jsx(Dropzone, {
                "data-hover": filesOver,
                onMouseDown: (e) => e.preventDefault(),
                onClick: onClickDropZone,
                onDragEnter: handleDragEnter,
                onDragOver: handleDragOver,
                onDragLeave: handleDragLeave,
                onDrop: handleDrop,
                children: dropZoneText
              }),
              /* @__PURE__ */ jsx("input", {
                type: "file",
                style: {
                  display: "none"
                },
                ref: inputRef,
                accept,
                onChange: (e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleUpload(e.target.files[0]);
                  }
                }
              })
            ]
          })
        })
      ]
    })
  });
};

export { EmptyComponent };
//# sourceMappingURL=EmptyComponent.js.map
